import { isPopDevToolsEnabled } from '../enabled';
import { popDevTools } from '../pop-devtools';
import type { PopDevToolsChannel, PopDevToolsLevel } from '../types';
import { isKnownPopDevToolsTag, resolvePopDevToolsChannelForTag } from '../channels';
import { compactString, summarizeArgs, summarizeValue } from './summary';

type ConsoleMethod = 'debug' | 'error' | 'info' | 'log' | 'warn';

const methods: readonly ConsoleMethod[] = ['debug', 'error', 'info', 'log', 'warn'];
const levelByMethod: Record<ConsoleMethod, PopDevToolsLevel> = {
  debug: 'debug',
  error: 'error',
  info: 'info',
  log: 'info',
  warn: 'warn',
};

let installed = false;
const originals = new Map<ConsoleMethod, (...args: unknown[]) => void>();

const exposeOriginalConsoleMethods = () => {
  (window as any).__popDevToolsOriginalConsole = Object.fromEntries(originals);
};

export interface FormattedConsoleArgs {
  channel: PopDevToolsChannel;
  level: PopDevToolsLevel;
  message: string;
  data: {
    args: unknown[];
    tag?: string;
  };
}

const parseLeadingConsoleTag = (value: unknown) => {
  if (typeof value !== 'string') return undefined;
  return value.match(/^\[([^\]]+)\]\s*/)?.[1];
};

export const formatConsoleArgsForPopDevTools = (
  method: ConsoleMethod,
  args: readonly unknown[],
): FormattedConsoleArgs => {
  const tag = parseLeadingConsoleTag(args[0]);
  const isKnownTag = tag ? isKnownPopDevToolsTag(tag) : false;
  const channel = tag && isKnownTag ? resolvePopDevToolsChannelForTag(tag) : 'console';
  const printableArgs = args.map((arg, index) => {
    if (index === 0 && typeof arg === 'string' && tag && isKnownTag) {
      return arg.replace(/^\[[^\]]+\]\s*/, '');
    }

    if (typeof arg === 'string') return arg;
    const summary = summarizeValue(arg);
    if (typeof summary === 'string') return summary;
    return compactString(JSON.stringify(summary) ?? String(summary));
  });
  const message = printableArgs.join(' ').trim() || `[${method}]`;

  return {
    channel,
    level: levelByMethod[method],
    message,
    data: {
      args: summarizeArgs(args),
      tag,
    },
  };
};

export const installConsoleTap = () => {
  if (installed || !isPopDevToolsEnabled()) return;
  installed = true;

  methods.forEach((method) => {
    const original = console[method].bind(console);
    originals.set(method, original);
    exposeOriginalConsoleMethods();
    console[method] = (...args: unknown[]) => {
      original(...args);
      // Re-check per call: a runtime opt-out leaves the wrapper installed but
      // must stop the per-log summarize/record work.
      if (!isPopDevToolsEnabled()) return;

      try {
        const formatted = formatConsoleArgsForPopDevTools(method, args);
        popDevTools.record(formatted);
      } catch (_error) {
        // Console behavior must never depend on the tap.
      }
    };
  });
};

export const uninstallConsoleTapForTests = () => {
  originals.forEach((original, method) => {
    console[method] = original;
  });
  originals.clear();
  delete (window as any).__popDevToolsOriginalConsole;
  installed = false;
};
